import {getEnv, getRoot, IModelType, types as t} from 'mobx-state-tree'

const NONE = Symbol()

type None = typeof NONE

export function createEnvInjector<Env> () {
  type InjectableEnv = {
    readonly [P in keyof Env]: Env[P]
  }

  type InjectableEnvName = keyof InjectableEnv

  type InjectedFromEnv<T> = T extends InjectableEnvName
    ? { $env: { [K in T]: InjectableEnv[T] } }
    : { }

  function injectFromEnv<
    T1 extends InjectableEnvName | None = None,
    T2 extends InjectableEnvName | None = None,
    T3 extends InjectableEnvName | None = None,
    T4 extends InjectableEnvName | None = None,
    T5 extends InjectableEnvName | None = None,
    T6 extends InjectableEnvName | None = None,
    T7 extends InjectableEnvName | None = None,
    T8 extends InjectableEnvName | None = None,
    T9 extends InjectableEnvName | None = None
    // Who needs more?
    > (
    n1: T1,
    n2?: T2,
    n3?: T3,
    n4?: T4,
    n5?: T5,
    n6?: T6,
    n7?: T7,
    n8?: T8,
    n9?: T9
  ): IModelType<{},
    InjectedFromEnv<T1>
    & InjectedFromEnv<T2>
    & InjectedFromEnv<T3>
    & InjectedFromEnv<T4>
    & InjectedFromEnv<T5>
    & InjectedFromEnv<T6>
    & InjectedFromEnv<T7>
    & InjectedFromEnv<T8>
    & InjectedFromEnv<T9>
    >
  function injectFromEnv (...names: InjectableEnvName[]) {
    return t.model()
      .views((self) => {
        /**
         * @note(SuperPaintman): Same reason.
         */
        let env: any = null
        const $env: any = {}
        const views = {
          get $env () {
            env = getEnv<Env>(self)

            return $env
          }
        }

        for (const name of names) {
          Object.defineProperty($env, name, {
            get () {
              return env[name]
            },
            enumerable: true
          })
        }

        return views
      })
  }

  return injectFromEnv
}

export function createRootInjector<InjectableModels> () {
  type InjectableModelsName = keyof InjectableModels

  type InjectedFromRoot<T> = T extends InjectableModelsName
    ? { $root: { [K in T]: InjectableModels[T] } }
    : { }

  function injectFromRoot<
    T1 extends InjectableModelsName | None = None,
    T2 extends InjectableModelsName | None = None,
    T3 extends InjectableModelsName | None = None,
    T4 extends InjectableModelsName | None = None,
    T5 extends InjectableModelsName | None = None,
    T6 extends InjectableModelsName | None = None,
    T7 extends InjectableModelsName | None = None,
    T8 extends InjectableModelsName | None = None,
    T9 extends InjectableModelsName | None = None
    // Who needs more?
    > (
    n1: T1,
    n2?: T2,
    n3?: T3,
    n4?: T4,
    n5?: T5,
    n6?: T6,
    n7?: T7,
    n8?: T8,
    n9?: T9
  ): IModelType<{},
    InjectedFromRoot<T1>
    & InjectedFromRoot<T2>
    & InjectedFromRoot<T3>
    & InjectedFromRoot<T4>
    & InjectedFromRoot<T5>
    & InjectedFromRoot<T6>
    & InjectedFromRoot<T7>
    & InjectedFromRoot<T8>
    & InjectedFromRoot<T9>
    >
  function injectFromRoot (...names: InjectableModelsName[]) {
    return t.model()
      .views((self) => {
        /**
         * @note(SuperPaintman):
         *    Because MobX doesn't cache our inner getters (in `$root`), we don't
         *    want to call `getRoot()` for each access to the property. That's why
         *    we cache the root manually.
         */
        let rootStore: any = null
        const $root: any = {}
        const views = {
          get $root () {
            rootStore = getRoot(self)

            return $root
          }
        }

        for (const name of names) {
          Object.defineProperty($root, name, {
            get () {
              return rootStore[name]
            },
            enumerable: true
          })
        }

        return views
      })
  }

  return injectFromRoot
}
